import { Box, Button, Stack, Typography } from '@mui/material'
import { QuizItem } from '@services/common.services'
import React, { useMemo } from 'react'
import _ from 'lodash'
import StepFormFields from './StepFormFields'
import { useWizard } from 'react-use-wizard'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'

interface Props {
  data?: QuizItem
  onSubmit: (data: any) => void
  onSubmitFinal: (data: any) => void
  formData?: any
  isSubmitting?: boolean
}

const StepContainer: React.FC<Props> = ({
  data,
  onSubmit,
  onSubmitFinal,
  formData,
  isSubmitting,
}) => {
  const { isLastStep, isFirstStep, previousStep, nextStep } = useWizard()

  const orderBy = useMemo(() => {
    return _.orderBy(data?.fields, ['order_id'], ['asc'])
  }, [data?.fields])

  const { t } = useTranslation()

  const methods = useForm({
    defaultValues: React.useMemo(() => {
      // Create default values from fields
      const defaults: Record<string, any> = {}
      orderBy?.forEach((field) => {
        defaults[field.field_id] =
          _.get(formData, `${field.field_id}`, undefined) ||
          field.default_value ||
          ''
      })
      return defaults
    }, [orderBy]),
  })

  const {
    handleSubmit,
    formState: { isValid, errors },
  } = methods

  const handleNext = async () => {
    if (isLastStep) {
      await handleSubmit(onSubmitFinal)()
      nextStep()
    } else {
      await handleSubmit(onSubmit)()
      nextStep()
    }
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ height: '100%', mb: 2 }}>
        <Typography variant="h2" align="center" mb={2}>
          {t(`QUIZ.${data?.title}`)}
        </Typography>

        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            width: '100%',
            maxHeight: {
              lg: 'calc(100vh - 400px)',
              md: 'auto',
              sm: 'auto',
              xs: 'auto',
            },
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            flexDirection: { lg: 'row', xs: 'column' },
            overflowX: 'hidden',
          }}
          className="scrollbar-container"
        >
          {data?.image && (
            <Box
              sx={{
                maxWidth: {
                  xs: 200,
                  md: 250,
                  lg: 300,
                },
                margin: '0 auto',
                mb: 2,
              }}
            >
              <Box
                sx={{ maxWidth: '100%', height: 'auto' }}
                component={'img'}
                src={data.image}
              />
            </Box>
          )}
          <Box
            sx={{
              height: 'auto',
              maxWidth: 800,
              margin: '0 auto',
            }}
          >
            {orderBy?.map((field, index) => {
              // Here, formValues is directly used, which should cause re-rendering when formValues changes
              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <StepFormFields
                    name={field.field_id}
                    data={field}
                    error={errors[field.field_id]?.message}
                  />
                </Box>
              )
            })}
          </Box>
        </Box>

        <Stack
          direction={'row'}
          sx={{ width: '100%' }}
          justifyContent={'space-between'}
          pb={2}
        >
          {!isFirstStep ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => previousStep()}
            >
              {t('SYSCOMMON.prev')}
            </Button>
          ) : (
            <Box></Box>
          )}
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isLastStep && isSubmitting}
            disabled={!isValid}
            onClick={handleNext}
          >
            {isLastStep ? t('SYSCOMMON.submit') : t('SYSCOMMON.next')}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export default StepContainer
